import { useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Send, CheckCircle2, Rocket } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { SITE } from "../lib/site";

/* Advertising inquiry form (/advertise).
   SUBJECTS and SCOPE_OPTIONS are data-driven: future advertising products
   (banner advertising, sponsored destinations, airline/airport features,
   featured travel services, newsletter advertising, sponsored articles,
   promoted listings, section sponsorships…) can be added here without
   redesigning the form or changing the backend. */

const SUBJECTS = [
  { id: "Advertising opportunities", label: "Advertising opportunities" },
  { id: "Advertising pricing / rates", label: "Advertising pricing / rates" },
  { id: "Sponsorship opportunity", label: "Sponsorship opportunity" },
  { id: "Partnership opportunity", label: "Partnership opportunity" },
  { id: "Affiliate / referral opportunity", label: "Affiliate / referral opportunity" },
  { id: "Featured listing", label: "Featured listing" },
  { id: "Other", label: "Other — please specify" },
];

const SCOPE_OPTIONS = [
  { id: "Sitewide Advertising", label: "Sitewide Advertising", desc: "Reach travelers throughout TravelerPulse." },
  { id: "Specific Sections", label: "Specific Sections", desc: "Advertise within one or more TravelerPulse sections." },
  { id: "Specific Pages", label: "Specific Pages", desc: "Advertise on selected pages, destinations, airlines, airports, routes, travel guides, or other content." },
  { id: "Featured / Sponsored Content", label: "Featured / Sponsored Content", desc: "Explore opportunities for sponsored articles, guides, features, or special placements." },
  { id: "Other", label: "Other — please specify", desc: "" },
];

const DURATIONS = ["One-time campaign", "One month", "3 months", "6 months", "12 months", "Ongoing", "Not sure — please recommend"];
const BUDGETS = ["Not decided", "Please provide available options", "I'll specify in the details"];
const CONTACT_METHODS = ["Email", "Phone", "Either"];

const AUDIENCES = [
  "Hotels", "Airlines", "Airports", "Destinations", "Travel agencies", "Tour operators",
  "Restaurants", "Car-rental companies", "Travel technology providers", "Financial institutions",
  "Insurance companies", "Retailers", "Local businesses", "Tourism organizations",
];

const input = "w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary";
const label = "block text-sm font-700 mb-1";

const FormSection = ({ title, children }) => (
  <div className="border border-zinc-300 bg-white p-6 space-y-4">
    <h2 className="font-heading font-700 text-lg md:text-lg uppercase tracking-wide border-b border-zinc-200 pb-2">{title}</h2>
    {children}
  </div>
);

const CheckOption = ({ checked, onToggle, title, desc, testid }) => (
  <label data-testid={testid}
    className={`flex gap-3 border p-3 cursor-pointer transition-colors ${checked ? "border-primary bg-blue-50/60" : "border-zinc-300 hover:border-zinc-400"}`}>
    <input type="checkbox" checked={checked} onChange={onToggle} className="mt-0.5 accent-[#1D4ED8]" />
    <span>
      <span className="text-sm font-700 block">{title}</span>
      {desc && <span className="text-xs text-muted-foreground block mt-0.5">{desc}</span>}
    </span>
  </label>
);

export default function Advertise() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", organization: "", address: "",
    phone: "", email: user?.email || "",
    subjects: [], subject_other: "",
    scope: [], scope_other: "", scope_details: "",
    details: "", comments: "",
    duration: "", start_date: "", budget: "", contact_method: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, id) => () => setForm((f) => ({
    ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
  }));

  const wantsTargeting = form.scope.includes("Specific Sections") || form.scope.includes("Specific Pages");

  const submit = async (e) => {
    e.preventDefault();
    if (form.subjects.length === 0) return toast.error("Please select at least one subject");
    if (form.scope.length === 0) return toast.error("Please select at least one advertising scope");
    setBusy(true);
    try {
      await api.post("/advertising/inquiries", {
        ...form,
        organization: form.organization || null,
        address: form.address || null,
        subject_other: form.subjects.includes("Other") ? form.subject_other : null,
        scope_other: form.scope.includes("Other") ? form.scope_other : null,
        scope_details: form.scope_details || null,
        comments: form.comments || null,
        duration: form.duration || null,
        start_date: form.start_date || null,
        budget: form.budget || null,
        contact_method: form.contact_method || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check the required fields and try again");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="advertise-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-heading font-900 text-3xl">Thank you!</h1>
        <p className="text-zinc-600 mt-3">
          Your advertising inquiry has been received. Our team will review your request and contact you with
          available advertising opportunities, formats, placement options, and pricing.
        </p>
        <Link to="/" className="inline-block mt-6 px-5 py-2.5 bg-primary text-white text-sm font-700 hover:bg-blue-700 transition-colors">Back to Home</Link>
      </div>
    );
  }

  return (
    <div data-testid="advertise-page">
      <PageHeader eyebrow={`Advertise With ${SITE.name}`} title="Put Your Brand in the Traveler's Journey" entityCode="ADVERTISE"
        unsplashQuery="airport departure boards travelers"
        subtitle={`Advertise on ${SITE.brandLabel} and reach travelers while they discover destinations, research airlines and airports, plan trips, prepare for departure, and navigate their journeys. Choose sitewide exposure or target specific sections, pages, destinations, airlines, airports, or travel topics.`} />

      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12 space-y-8">
        <section className="space-y-4 text-zinc-700 leading-relaxed" data-testid="advertise-intro">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight">Reach Travelers Where They Plan, Book, Explore &amp; Share</h2>
          <p>
            {SITE.brandLabel} offers businesses and organizations an opportunity to connect with travelers through
            relevant advertising across travel content, destinations, airlines, airports, transportation, travel
            services, and specialized travel sections.
          </p>
          <p>
            Whether you want sitewide visibility or want to reach travelers on a specific section, destination,
            airline, airport, or travel topic, we'd love to hear from you.
          </p>
          <div className="border-l-4 border-primary bg-blue-50/60 px-5 py-4">
            <div className="flex items-center gap-2 font-heading font-700 text-lg text-zinc-900"><Megaphone className="w-5 h-5 text-primary" /> Advertising Inquiry</div>
            <p className="text-sm mt-1">
              Please complete the form below and tell us what you're interested in. Our team will review your request
              and contact you with available advertising opportunities, formats, placement options, and pricing.
            </p>
          </div>
        </section>

        <form onSubmit={submit} className="space-y-6" data-testid="advertise-form">
          <FormSection title="Your Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>First Name *</label>
                <input value={form.first_name} onChange={set("first_name")} required data-testid="ad-first-name" className={input} />
              </div>
              <div>
                <label className={label}>Last Name *</label>
                <input value={form.last_name} onChange={set("last_name")} required data-testid="ad-last-name" className={input} />
              </div>
            </div>
            <div>
              <label className={label}>Organization / Company Name</label>
              <input value={form.organization} onChange={set("organization")} data-testid="ad-organization" className={input} />
            </div>
            <div>
              <label className={label}>Address</label>
              <textarea value={form.address} onChange={set("address")} rows={2} data-testid="ad-address" className={`${input} resize-y`} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Phone Number *</label>
                <input value={form.phone} onChange={set("phone")} required type="tel" placeholder="+1 555 123 4567" data-testid="ad-phone" className={input} />
              </div>
              <div>
                <label className={label}>Email Address *</label>
                <input value={form.email} onChange={set("email")} required type="email" data-testid="ad-email" className={input} />
              </div>
            </div>
          </FormSection>

          <FormSection title="Inquiry">
            <div>
              <label className={label}>Subject * <span className="font-400 text-muted-foreground">— What are you contacting us about? Select all that apply.</span></label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {SUBJECTS.map((s) => (
                  <CheckOption key={s.id} checked={form.subjects.includes(s.id)} onToggle={toggle("subjects", s.id)}
                    title={s.label} testid={`ad-subject-${s.id.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                ))}
              </div>
              {form.subjects.includes("Other") && (
                <input value={form.subject_other} onChange={set("subject_other")} required placeholder="Please specify…"
                  data-testid="ad-subject-other-input" className={`${input} mt-2`} />
              )}
            </div>
          </FormSection>

          <FormSection title="What Are You Interested In?">
            <div>
              <label className={label}>Advertising Scope * <span className="font-400 text-muted-foreground">— Select one or more.</span></label>
              <div className="space-y-2 mt-2">
                {SCOPE_OPTIONS.map((s) => (
                  <CheckOption key={s.id} checked={form.scope.includes(s.id)} onToggle={toggle("scope", s.id)}
                    title={s.label} desc={s.desc} testid={`ad-scope-${s.id.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                ))}
              </div>
              {form.scope.includes("Other") && (
                <input value={form.scope_other} onChange={set("scope_other")} required placeholder="Please specify…"
                  data-testid="ad-scope-other-input" className={`${input} mt-2`} />
              )}
            </div>
            {wantsTargeting && (
              <div data-testid="ad-scope-details-block">
                <label className={label}>Which sections, pages, destinations, airlines, airports, or topics are you interested in?</label>
                <textarea value={form.scope_details} onChange={set("scope_details")} rows={3} data-testid="ad-scope-details"
                  placeholder="e.g. Airport pages for JFK and LAX, the Visas & Embassies section, destination pages for Japan…"
                  className={`${input} resize-y`} />
              </div>
            )}
          </FormSection>

          <FormSection title="Advertising Details">
            <div>
              <label className={label}>Details * <span className="font-400 text-muted-foreground">— Tell us about your advertising needs.</span></label>
              <textarea value={form.details} onChange={set("details")} required rows={6} data-testid="ad-details"
                placeholder="Please describe your business, product or service, your target audience, preferred advertising locations, campaign objectives, and any other information that would help us understand your needs."
                className={`${input} resize-y`} />
            </div>
            <div>
              <label className={label}>Comments / Additional Information</label>
              <textarea value={form.comments} onChange={set("comments")} rows={3} data-testid="ad-comments" className={`${input} resize-y`} />
            </div>
          </FormSection>

          <FormSection title="Campaign Preferences">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Preferred Campaign Duration</label>
                <select value={form.duration} onChange={set("duration")} data-testid="ad-duration" className={`${input} bg-white`}>
                  <option value="">Select…</option>
                  {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Preferred Start Date</label>
                <input value={form.start_date} onChange={set("start_date")} type="date" data-testid="ad-start-date" className={input} />
              </div>
              <div>
                <label className={label}>Approximate Advertising Budget</label>
                <select value={form.budget} onChange={set("budget")} data-testid="ad-budget" className={`${input} bg-white`}>
                  <option value="">Select…</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Preferred Contact Method</label>
                <select value={form.contact_method} onChange={set("contact_method")} data-testid="ad-contact-method" className={`${input} bg-white`}>
                  <option value="">Select…</option>
                  {CONTACT_METHODS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          <div className="border border-zinc-300 bg-white p-6" data-testid="advertise-cta">
            <div className="flex items-center gap-2 font-heading font-700 text-lg text-zinc-900 mb-2">
              <Rocket className="w-5 h-5 text-primary" /> Let's Talk About Your Campaign
            </div>
            <p className="text-sm text-zinc-700 mb-3">
              Whether you're a travel-related organization of any kind, {SITE.name} can help you reach travelers at
              relevant points throughout their journey:
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5">
              {AUDIENCES.map((a) => (
                <span key={a} className="text-xs border border-zinc-200 bg-zinc-50 px-2 py-1 text-zinc-700">{a}</span>
              ))}
            </div>
            <button type="submit" disabled={busy} data-testid="advertise-submit"
              className="inline-flex items-center gap-2 bg-primary text-white font-700 px-8 py-3 text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Inquiry"}
            </button>
            <p className="text-xs text-muted-foreground mt-3">
              By submitting this form, you agree that {SITE.name} may contact you regarding your advertising inquiry
              and related opportunities.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
